import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Target, Trophy, Pencil, Flame } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Confetti } from "@/components/Celebration";

const TYPES = [["lessons", "Lessons completed", 5], ["tasks", "Planner tasks done", 10], ["minutes", "Minutes learned", 120]];
const input = "w-full px-4 py-2.5 rounded-xl bg-white border border-[#E6DEC3] text-sm focus:outline-none focus:border-[#2C4A3E]";

const GoalDialog = ({ kid, goal, open, setOpen, onSaved }) => {
  const [f, setF] = useState({ type: "lessons", target: 5 });
  useEffect(() => { if (open) setF(goal ? { type: goal.type, target: goal.target } : { type: "lessons", target: 5 }); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (e) => {
    e.preventDefault();
    try { await http.put(`/family/children/${kid.student_id}/goal`, { type: f.type, target: Number(f.target) }); toast.success(`Goal set for ${kid.name.split(" ")[0]}`); setOpen(false); onSaved(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const clear = async () => { await http.delete(`/family/children/${kid.student_id}/goal`); setOpen(false); onSaved(); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#FDFBF7]" data-testid="goal-dialog">
        <DialogTitle className="font-display text-2xl text-[#2C4A3E]">Weekly goal for {kid.name.split(" ")[0]}</DialogTitle>
        <form onSubmit={save} className="space-y-3">
          <div className="grid gap-2">{TYPES.map(([t, label, def]) => (
            <label key={t} className={`flex items-center gap-3 px-4 py-3 rounded-2xl border cursor-pointer ${f.type === t ? "border-[#2C4A3E] bg-[#F1EADB]" : "border-[#E6DEC3] bg-white"}`}>
              <input type="radio" name="goal-type" checked={f.type === t} onChange={() => setF({ type: t, target: def })} data-testid={`goal-type-${t}`} />{label}
            </label>))}</div>
          <label className="block text-sm text-[#2C4A3E]">Target this week
            <input type="number" min={1} max={1000} required value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })} data-testid="goal-target-input" className={`${input} mt-1`} /></label>
          <button data-testid="goal-save-btn" className="w-full py-3 rounded-full bg-[#C87D53] text-white">Save goal</button>
          {goal && <button type="button" onClick={clear} data-testid="goal-clear-btn" className="w-full text-sm underline text-stone-500">Remove goal</button>}
        </form>
      </DialogContent>
    </Dialog>
  );
};

const GoalRow = ({ k, onSaved }) => {
  const [open, setOpen] = useState(false);
  const g = k.goal;
  return (
    <div className={`rounded-3xl border p-5 flex flex-wrap items-center gap-4 ${g?.met ? "bg-[#FBF3E2] border-[#F2D49B]" : "bg-white/80 border-[#E6DEC3]"}`} data-testid={`goal-row-${k.student_id}`}>
      <div className="w-11 h-11 rounded-full grid place-items-center bg-[#F2D49B] text-[#5B3A1E]">{g?.met ? <Trophy className="w-5 h-5" /> : <Target className="w-5 h-5" />}</div>
      <div className="flex-1 min-w-[200px]">
        <p className="font-display text-lg text-[#2C4A3E]">{k.name.split(" ")[0]}{g ? `: ${g.target} ${g.label}` : ""}</p>
        {g ? <Progress value={Math.min(100, (100 * g.progress) / g.target)} className="h-2 mt-2" /> : <p className="text-sm text-stone-500">No weekly goal yet</p>}
      </div>
      {g?.streak > 0 && <span className="inline-flex items-center gap-1 text-sm text-[#8A3A12]" data-testid={`goal-streak-${k.student_id}`}><Flame className="w-4 h-4" /> {g.streak} wk streak</span>}
      {g && <span className="text-sm font-medium text-[#2C4A3E]" data-testid={`goal-progress-${k.student_id}`}>{g.met ? "Goal met!" : `${g.progress} / ${g.target}`}</span>}
      <button onClick={() => setOpen(true)} data-testid={`goal-edit-${k.student_id}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#E6DEC3] bg-white text-sm"><Pencil className="w-3.5 h-3.5" />{g ? "Change" : "Set goal"}</button>
      <GoalDialog kid={k} goal={g} open={open} setOpen={setOpen} onSaved={onSaved} />
    </div>
  );
};

export const FamilyGoals = ({ goals, only, onSaved }) => {
  const shown = goals?.filter((k) => !only || k.student_id === only);
  const party = shown?.filter((k) => k.goal?.celebrate) || [];
  useEffect(() => {
    if (!party.length) return;
    party.forEach((k) => { toast.success(`${k.name.split(" ")[0]} met this week's goal!${k.goal.streak_badge ? ` ${k.goal.streak_badge} badge earned!` : ""}`); http.post(`/family/children/${k.student_id}/goal/seen`).catch(() => {}); });
  }, [party.map((k) => k.student_id).join()]); // eslint-disable-line react-hooks/exhaustive-deps
  const [boom, setBoom] = useState(false);
  useEffect(() => { if (party.length) { setBoom(true); const t = setTimeout(() => setBoom(false), 4000); return () => clearTimeout(t); } }, [party.length]);
  if (!shown?.length) return null;
  return (
    <section className="mt-8" data-testid="family-goals">
      <p className="text-xs uppercase tracking-widest text-stone-500 mb-3">Weekly family goals</p>
      <div className="grid gap-3">{shown.map((k) => <GoalRow key={k.student_id} k={k} onSaved={onSaved} />)}</div>
      {boom && createPortal(<div data-testid="goal-confetti" className="relative z-[80]"><Confetti /></div>, document.body)}
    </section>
  );
};
